import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import KibDivider from '@chewy/kib-divider-react'
import { KibChevronRightIcon } from '@chewy/kib-icons-react'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react'
import { KibTooltip } from '@chewy/kib-tooltip-react'
import { last } from 'ramda'
import { PermissionArea } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import { addSOAP, finalizeSoap, reOpenSoap } from '~/store/actions/soap'
import { fetchSoapCharges, getCharges } from '~/store/duck/charges'
import { useOpenInvoice } from '~/store/hooks/finance'
import {
  getCRUDByArea,
  getCurrentBusiness,
  getIsPracticeAdministrator,
} from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getSelectedOrders } from '~/store/reducers/orders'
import {
  getAppointmentId,
  getClientId,
  getInvoiceId,
  getIsFinalized,
  getPatientId,
  getSoapBusinessId,
  getSoapId,
  getSOAPisFetching,
} from '~/store/reducers/soap'
import { getTimetableEvent } from '~/store/reducers/timetable'
import { addOriginalBusinessId } from '~/utils'
import { isChargesInvoiceType } from '~/utils/finance'
import useIsDymoConnectEnabled from '~/utils/useIsDymoEnabled'

import useConfirmLeaveSoapForAssignmentAlert from '../useConfirmLeaveSoapForAssignment'
import useConfirmLockSoapAlert from '../useConfirmLockSoapAlert'
import { useCanUpdateSignature } from '../utils/useCanUpdateSignature'
import { useIsSoapCustomizationEnabled } from '../utils/useIsSoapCustomizationEnabled'
import { useMissingVaccineInfo } from '../utils/useMissingVaccineInfo'
import useSoapMenuDialogs from './useSoapMenuDialogs'
import { useSoapMenuReportCard } from './useSoapMenuReportCard'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import '@chewy/kib-tag-styles/dist/kib-tag.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-inline-alert.css'
import '@chewy/kib-divider-styles/dist/kib-divider.css'
import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import styles from './RhapSoapHeaderMenu.module.scss'

const RhapSoapHeaderMenu = ({
  triggerRef,
}: {
  triggerRef: React.RefObject<HTMLButtonElement>
}) => {
  const { t } = useTranslation(['Common', 'TimeTable'])

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const business = useSelector(getCurrentBusiness)
  const clientId = useSelector(getClientId)
  const patientId = useSelector(getPatientId)
  const appointmentId = useSelector(getAppointmentId)
  const invoiceId = useSelector(getInvoiceId)
  const soapId = useSelector(getSoapId)
  const soapBusinessId = useSelector(getSoapBusinessId)
  const soapIsFetching = useSelector(getSOAPisFetching)
  const appointment = useSelector(getTimetableEvent(appointmentId))
  const SOAPPermissions = useSelector(getCRUDByArea(PermissionArea.SOAP))
  const invoicePermissions = useSelector(getCRUDByArea(PermissionArea.INVOICE))
  const taskPermissions = useSelector(getCRUDByArea(PermissionArea.TASK))
  const appointmentPermissions = useSelector(
    getCRUDByArea(PermissionArea.EVENT_APPOINTMENT),
  )
  const communicationPermissions = useSelector(
    getCRUDByArea(PermissionArea.COMMUNICATION),
  )
  const patientPermissions = useSelector(getCRUDByArea(PermissionArea.PATIENT))
  const businessPermissions = useSelector(
    getCRUDByArea(PermissionArea.BUSINESS),
  )
  const isPracticeAdmin = useSelector(getIsPracticeAdministrator(business))
  const isFinalized = useSelector(getIsFinalized)
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )
  const isCvcRolesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_ROLES),
  )
  const clientCharges = useSelector(getCharges)
  const { showReportCard } = useSoapMenuReportCard()
  const isSoapCustomizationEnabled = useIsSoapCustomizationEnabled()
  const missingVaccineInfo = useMissingVaccineInfo()
  const isMissingVaccineInfo = missingVaccineInfo.length > 0
  const isOnlyMissingSignature =
    missingVaccineInfo.filter(
      (vaccineDetails) =>
        'signature' in vaccineDetails &&
        Object.keys(vaccineDetails).length === 1,
    ).length > 0
  const canUpdateSignature = useCanUpdateSignature()
  const isInvoice = isChargesInvoiceType(clientCharges)
  const isDymoConnectEnabled = useIsDymoConnectEnabled()
  const appointmentSoaps = appointment?.soaps
  const updateSOAPDisabled =
    !SOAPPermissions.update ||
    (isMissingVaccineInfo && business?.autoGenerateVaccineCertificates)
  const createInvoiceDisabled = !invoicePermissions.create

  const {
    openAppointmentDialog,
    openSoapAttachmentDialog,
    openNewConversationDialog,
    openDiagnosisHistoryDialog,
    openNoteHistoryDialog,
    openAddReminderDialog,
    openVaccineHistoryDialog,
    openVitalsDialog,
    openEstimatesListDialog,
    openAppointmentReportCardDialog,
    openPrintCageLabelDialog,
    openPrintFolderLabelDialog,
    openPrintPracticeAddressLabelDialog,
    openTaskDialog,
    openAddendumDialog,
    openVaccineCertificateValidationDialog,
  } = useSoapMenuDialogs({ soapId })

  const [navigateToNewSoap, setNavigateToNewSoap] = useState(false)

  const [openConfirmLockSoapAlert] = useConfirmLockSoapAlert()
  const [openConfirmLeaveSoapForAssignmentAlert] =
    useConfirmLeaveSoapForAssignmentAlert()

  const addButtonRef = useRef<HTMLButtonElement>(null)
  const labelButtonRef = useRef<HTMLButtonElement>(null)

  const handleFinalizeSoap = () =>
    openConfirmLockSoapAlert(() => dispatch(finalizeSoap()))
  const handleReOpenSoap = () => dispatch(reOpenSoap())
  const openInvoice = useOpenInvoice(clientId)
  const selectedOrders = useSelector(getSelectedOrders)

  const handleAddSOAP = () => {
    dispatch(
      addSOAP(appointmentId, isPatientSharingEnabled ? soapBusinessId : null),
    )
    setNavigateToNewSoap(true)
  }

  const handleOpenInvoice = () => {
    openInvoice({
      clientId,
      eventId: appointmentId,
      invoiceId,
      patientId,
      soapId,
      soapBusinessId,
      id: invoiceId,
    })
  }

  const addMenuItems = [
    {
      label: t('Menu:SOAP_TOP_MENU.APPOINTMENT_NEW'),
      onClick: openConfirmLeaveSoapForAssignmentAlert,
      disabled: !appointmentPermissions.create,
    },
    isFinalized && {
      label: t('Common:ADDENDUM'),
      onClick: () => openAddendumDialog({ soapId, patientId }),
      disabled: !SOAPPermissions.update,
    },
    {
      label: t('Common:ATTACHMENT'),
      onClick: () =>
        openSoapAttachmentDialog({
          clientId,
          patientId,
          soapId,
          soapBusinessId,
        }),
      disabled: !SOAPPermissions.update,
    },
    {
      label: t('Common:CONVERSATION'),
      onClick: () =>
        openNewConversationDialog({ appointmentId, clientId, patientId }),
      disabled: !communicationPermissions.create,
    },
    {
      label: t('Common:DIAGNOSIS_HISTORY'),
      onClick: () => openDiagnosisHistoryDialog({ clientId, patientId }),
      disabled: !(!isCvcRolesEnabled || patientPermissions.update),
    },
    {
      label: t('Menu:SOAP_TOP_MENU.NOTE_TIMELINE'),
      onClick: () => openNoteHistoryDialog({ clientId, patientId }),
      disabled: !(!isCvcRolesEnabled || patientPermissions.update),
    },
    {
      label: t('Common:REMINDER'),
      onClick: () => openAddReminderDialog({ patientId }),
      disabled: !SOAPPermissions.update,
    },
    {
      label: t('Menu:SOAP_TOP_MENU.SOAP_TO_CURRENT_APPOINTMENT'),
      onClick: () => handleAddSOAP(),
      disabled: !SOAPPermissions.create,
    },
    {
      label: t('Common:TASK'),
      onClick: () =>
        openTaskDialog({
          clientId,
          patientId,
          appointmentId,
          soapId,
          dueDate: appointment?.scheduledEndDatetime,
        }),
      disabled: !taskPermissions.create,
    },
    {
      label: t('Common:VACCINE_HISTORY'),
      onClick: () => openVaccineHistoryDialog({ clientId, patientId }),
      disabled: !(!isCvcRolesEnabled || patientPermissions.update),
    },
    {
      label: t('Common:VITALS'),
      onClick: () => openVitalsDialog({ clientId, patientId }),
      disabled: !(!isCvcRolesEnabled || patientPermissions.update),
    },
  ]

  const labelMenuItems = [
    {
      label: t('Common:PRINT_CAGE_CARD_LABEL'),
      onClick: () => openPrintCageLabelDialog({ clientId, patientId }),
      disabled: !patientPermissions.read,
    },
    isDymoConnectEnabled && {
      label: t('Common:PRINT_FOLDER_LABEL'),
      onClick: () => openPrintFolderLabelDialog({ clientId, patientId }),
      disabled: !patientPermissions.read,
    },
    isDymoConnectEnabled && {
      label: t('Common:PRINT_PRACTICE_ADDRESS_LABEL'),
      onClick: () => openPrintPracticeAddressLabelDialog(),
      disabled: !businessPermissions.read,
    },
  ]

  const handleAddMissingRabiesInfo = () => {
    openVaccineCertificateValidationDialog({
      clientId,
      patientId,
      vaccines: selectedOrders,
    })
  }

  useEffect(() => {
    if (navigateToNewSoap && appointmentSoaps?.length) {
      const lastSoapId = last(appointmentSoaps)?.id
      const url = isSoapCustomizationEnabled
        ? `/soap/${lastSoapId}`
        : `/soap/${lastSoapId}/appointment`
      navigate(
        addOriginalBusinessId(
          url,
          isPatientSharingEnabled ? appointment?.businessId : null,
        ),
      )
      setNavigateToNewSoap(false)
    }
  }, [appointmentSoaps?.length])

  useEffect(() => {
    if (clientId && soapId) {
      dispatch(fetchSoapCharges({ id: clientId, soapId }))
    }
  }, [clientId, soapId])

  return (
    <KibTooltip
      className={styles.tooltip}
      placement="bottom"
      popperModifiers={[
        {
          name: 'offset',
          options: {
            offset: [-60, 10],
          },
        },
        {
          name: 'flip',
          enabled: false,
        },
      ]}
      trigger={triggerRef}
    >
      <div className={styles.tooltipContent}>
        {!isFinalized && (
          <div className={styles.finalizeContentContainer}>
            <KibButtonNew
              disabled={updateSOAPDisabled}
              theme="transactional"
              onClick={handleFinalizeSoap}
            >
              {t('Common:FINALIZE_ACTION')}
            </KibButtonNew>
            {business?.autoGenerateVaccineCertificates &&
              isMissingVaccineInfo && (
                <div className={styles.alertContainer}>
                  <KibTopLevelAlert severity="warning">
                    <p>{t('Common:SOAP_VACCINE_ERROR')}</p>
                    {isOnlyMissingSignature && !canUpdateSignature ? (
                      <p>{t('Common:SOAP_VACCINE_SIGNATURE_ERROR')}</p>
                    ) : (
                      <KibButtonNew
                        className={styles.addNowButton}
                        size="small"
                        onClick={handleAddMissingRabiesInfo}
                      >
                        {t('Common:ADD_NOW')}
                      </KibButtonNew>
                    )}
                  </KibTopLevelAlert>
                </div>
              )}
          </div>
        )}
        {isFinalized && isPracticeAdmin && (
          <>
            <div className={styles.reopenSoapButtonContainer}>
              <KibButtonNew theme="utility" onClick={handleReOpenSoap}>
                {t('Common:RE-OPEN_SOAP')}
              </KibButtonNew>
            </div>
            <KibDivider className={styles.divider} />
          </>
        )}
        {!isFinalized && <KibDivider className={styles.divider} />}
        <div className={styles.menuContainer}>
          <KibButtonNew
            className={styles.menuButton}
            disabled={soapIsFetching}
            emphasis="tertiary"
            icon={<KibChevronRightIcon />}
            iconPosition="end"
            ref={addButtonRef}
            theme="utility"
          >
            {t('Common:ADD_ACTION')}
          </KibButtonNew>
          <KibTooltip
            className={styles.tooltip}
            placement="left"
            popperModifiers={[
              {
                name: 'offset',
                options: {
                  offset: [isFinalized ? 220 : 125, 5],
                },
              },
              {
                name: 'flip',
                enabled: false,
              },
            ]}
            trigger={addButtonRef}
          >
            <ul className={styles.menuContainer}>
              {addMenuItems.map((button) => {
                if (button) {
                  return (
                    <li key={`${button.label}-add-menu-item`}>
                      <KibButtonNew
                        className={styles.menuButton}
                        disabled={button.disabled}
                        emphasis="tertiary"
                        theme="utility"
                        onClick={button.onClick}
                      >
                        {button.label}
                      </KibButtonNew>
                    </li>
                  )
                }
                return null
              })}
            </ul>
          </KibTooltip>

          <KibButtonNew
            className={styles.menuButton}
            disabled={createInvoiceDisabled || soapIsFetching}
            emphasis="tertiary"
            theme="utility"
            onClick={() => {
              openAppointmentDialog({
                clientId,
                appointmentId,
                patientId,
              })
            }}
          >
            {t('TimeTable:ADD_APPOINTMENT_COMPONENT.EDIT_CURRENT_APPOINTMENT')}
          </KibButtonNew>

          {!isFinalized && (
            <KibButtonNew
              className={styles.menuButton}
              disabled={updateSOAPDisabled || createInvoiceDisabled}
              emphasis="tertiary"
              theme="utility"
              onClick={() => {
                openEstimatesListDialog({
                  patientId,
                })
              }}
            >
              {t('Common:GO_TO_ESTIMATE')}
            </KibButtonNew>
          )}

          <KibButtonNew
            className={styles.menuButton}
            disabled={createInvoiceDisabled || soapIsFetching}
            emphasis="tertiary"
            theme="utility"
            onClick={handleOpenInvoice}
          >
            {isInvoice
              ? t('Common:GO_TO_INVOICE')
              : t('Common:GO_TO_CHARGE_SHEET')}
          </KibButtonNew>

          <KibButtonNew
            className={styles.menuButton}
            emphasis="tertiary"
            icon={<KibChevronRightIcon />}
            iconPosition="end"
            ref={labelButtonRef}
            theme="utility"
          >
            {t('Common:LABEL')}
          </KibButtonNew>
          <KibTooltip
            className={styles.tooltip}
            placement="left"
            popperModifiers={[
              {
                name: 'offset',
                options: {
                  offset: [3, 5],
                },
              },
              {
                name: 'flip',
                enabled: false,
              },
            ]}
            trigger={labelButtonRef}
          >
            <ul className={styles.menuContainer}>
              {labelMenuItems.map((button) => {
                if (button) {
                  return (
                    <li key={`${button.label}-label-menu-item`}>
                      <KibButtonNew
                        className={styles.menuButton}
                        disabled={button.disabled}
                        emphasis="tertiary"
                        key={button.label}
                        theme="utility"
                        onClick={button.onClick}
                      >
                        {button.label}
                      </KibButtonNew>
                    </li>
                  )
                }
                return null
              })}
            </ul>
          </KibTooltip>
          {showReportCard && (
            <KibButtonNew
              className={styles.menuButton}
              emphasis="tertiary"
              theme="utility"
              onClick={() => openAppointmentReportCardDialog({ soapId })}
            >
              {t('Menu:SOAP_TOP_MENU.CREATE_REPORT_CARD')}
            </KibButtonNew>
          )}
        </div>
      </div>
    </KibTooltip>
  )
}

export default RhapSoapHeaderMenu
